import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaCalendarAlt, FaExclamationCircle, FaClock, FaCheckCircle,
    FaMagic, FaSpinner, FaChartBar
} from 'react-icons/fa';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
    ResponsiveContainer, AreaChart, Area, CartesianGrid, Legend
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axiosInstance from '../utils/axiosConfig';

const SEVERIDAD_COLORS = {
    baja: '#10B981',
    media: '#F59E0B',
    alta: '#EF4444',
    critica: '#7F1D1D'
};

const SEVERIDAD_LABELS = {
    baja: 'Baja',
    media: 'Media',
    alta: 'Alta',
    critica: 'Crítica'
};

const PERIODOS = [
    { label: '7 días', value: 7 },
    { label: '15 días', value: 15 },
    { label: '30 días', value: 30 },
    { label: '90 días', value: 90 }
];

const formatMinutes = (min) => {
    if (!min && min !== 0) return '—';
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.08, duration: 0.4, ease: 'easeOut' }
    })
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-100 text-sm">
            <p className="font-medium text-gray-700 mb-1">{label}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ color: entry.color || entry.fill }} className="text-sm">
                    {entry.name}: <span className="font-semibold">{entry.value}</span>
                </p>
            ))}
        </div>
    );
};

const EventosDashboard = () => {
    const [dias, setDias] = useState(30);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/eventos/estadisticas?dias=${dias}`);
            setStats(res.data);
        } catch (err) {
            console.error('Error cargando estadísticas:', err);
        } finally {
            setLoading(false);
        }
    }, [dias]);

    useEffect(() => {
        fetchStats();
        setAiAnalysis(null);
    }, [fetchStats]);

    const handleAiAnalysis = async () => {
        setAiLoading(true);
        try {
            const res = await axiosInstance.post('/api/eventos/ia/correlacion', { dias });
            setAiAnalysis(res.data.analysis);
        } catch (err) {
            console.error('Error en análisis IA:', err);
            setAiAnalysis('No se pudo completar el análisis. Intente nuevamente.');
        } finally {
            setAiLoading(false);
        }
    };

    // Prepare chart data
    const severidadData = stats?.por_severidad
        ? Object.entries(stats.por_severidad).map(([key, value]) => ({
            name: SEVERIDAD_LABELS[key] || key,
            value,
            color: SEVERIDAD_COLORS[key] || '#9CA3AF'
        }))
        : [];

    const categoriaData = stats?.por_categoria || [];

    const tendenciaData = stats?.tendencia || [];

    const sedeData = stats?.por_sede
        ? Object.entries(stats.por_sede)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
        : [];

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center py-20">
                <FaSpinner className="animate-spin text-sura-blue text-2xl" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Period selector */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FaChartBar className="text-sura-blue" />
                    <h2 className="text-lg font-bold text-gray-800">Estadísticas de Eventos</h2>
                </div>
                <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
                    {PERIODOS.map((p) => (
                        <button
                            key={p.value}
                            onClick={() => setDias(p.value)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                                dias === p.value
                                    ? 'bg-sura-blue text-white shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    {
                        label: 'Total eventos',
                        value: stats?.total_eventos ?? 0,
                        icon: FaCalendarAlt,
                        color: 'text-sura-blue',
                        bg: 'bg-blue-50'
                    },
                    {
                        label: 'Abiertos actuales',
                        value: stats?.abiertos_actuales ?? 0,
                        icon: FaExclamationCircle,
                        color: 'text-amber-600',
                        bg: 'bg-amber-50'
                    },
                    {
                        label: 'Tiempo prom. resolución',
                        value: formatMinutes(stats?.tiempo_promedio_resolucion),
                        icon: FaClock,
                        color: 'text-purple-600',
                        bg: 'bg-purple-50'
                    },
                    {
                        label: 'Resueltos',
                        value: stats?.total_resueltos ?? 0,
                        icon: FaCheckCircle,
                        color: 'text-emerald-600',
                        bg: 'bg-emerald-50'
                    }
                ].map((kpi, i) => (
                    <motion.div
                        key={kpi.label}
                        custom={i}
                        initial="hidden"
                        animate="visible"
                        variants={cardVariants}
                        className="bg-white rounded-xl p-5 shadow-card border border-gray-100"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-gray-500 font-medium">{kpi.label}</span>
                            <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                                <kpi.icon className={`${kpi.color} text-base`} />
                            </div>
                        </div>
                        <p className="text-2xl font-bold text-gray-800">{kpi.value}</p>
                    </motion.div>
                ))}
            </div>

            {/* Charts 2x2 grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Severidad Donut */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                    className="bg-white rounded-xl p-5 shadow-card border border-gray-100"
                >
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Por severidad</h3>
                    {severidadData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={severidadData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={90}
                                    paddingAngle={3}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {severidadData.map((entry, i) => (
                                        <Cell key={i} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    formatter={(value) => (
                                        <span className="text-xs text-gray-600">{value}</span>
                                    )}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-60 flex items-center justify-center text-gray-400 text-sm">
                            Sin datos disponibles
                        </div>
                    )}
                </motion.div>

                {/* Categoría Bar Chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.4 }}
                    className="bg-white rounded-xl p-5 shadow-card border border-gray-100"
                >
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Por categoría</h3>
                    {categoriaData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={categoriaData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="nombre"
                                    tick={{ fontSize: 11, fill: '#6B7280' }}
                                    axisLine={{ stroke: '#E5E7EB' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: '#6B7280' }}
                                    axisLine={false}
                                    tickLine={false}
                                    allowDecimals={false}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" name="Eventos" radius={[4, 4, 0, 0]}>
                                    {categoriaData.map((entry, i) => (
                                        <Cell key={i} fill={entry.color || '#3B82F6'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-60 flex items-center justify-center text-gray-400 text-sm">
                            Sin datos disponibles
                        </div>
                    )}
                </motion.div>

                {/* Tendencia Area Chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                    className="bg-white rounded-xl p-5 shadow-card border border-gray-100"
                >
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Tendencia diaria</h3>
                    {tendenciaData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={240}>
                            <AreaChart data={tendenciaData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                <defs>
                                    <linearGradient id="colorEventos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#003366" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#003366" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="fecha"
                                    tick={{ fontSize: 10, fill: '#6B7280' }}
                                    axisLine={{ stroke: '#E5E7EB' }}
                                    tickLine={false}
                                    tickFormatter={(v) => {
                                        const d = new Date(v + 'T00:00:00');
                                        return `${d.getDate()}/${d.getMonth() + 1}`;
                                    }}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: '#6B7280' }}
                                    axisLine={false}
                                    tickLine={false}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    content={<CustomTooltip />}
                                    labelFormatter={(v) => {
                                        const d = new Date(v + 'T00:00:00');
                                        return d.toLocaleDateString('es-VE', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric'
                                        });
                                    }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="count"
                                    name="Eventos"
                                    stroke="#003366"
                                    strokeWidth={2}
                                    fill="url(#colorEventos)"
                                    dot={{ r: 3, fill: '#003366', strokeWidth: 0 }}
                                    activeDot={{ r: 5, fill: '#003366', strokeWidth: 2, stroke: '#fff' }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-60 flex items-center justify-center text-gray-400 text-sm">
                            Sin datos disponibles
                        </div>
                    )}
                </motion.div>

                {/* Sede Horizontal Bar Chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45, duration: 0.4 }}
                    className="bg-white rounded-xl p-5 shadow-card border border-gray-100"
                >
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Por sede</h3>
                    {sedeData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart
                                data={sedeData}
                                layout="vertical"
                                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                <XAxis
                                    type="number"
                                    tick={{ fontSize: 11, fill: '#6B7280' }}
                                    axisLine={false}
                                    tickLine={false}
                                    allowDecimals={false}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={50}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar
                                    dataKey="value"
                                    name="Eventos"
                                    fill="#003366"
                                    radius={[0, 4, 4, 0]}
                                    barSize={24}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-60 flex items-center justify-center text-gray-400 text-sm">
                            Sin datos disponibles
                        </div>
                    )}
                </motion.div>
            </div>

            {/* AI Analysis */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="bg-white rounded-xl p-5 shadow-card border border-gray-100"
            >
                <AnimatePresence mode="wait">
                    {!aiAnalysis ? (
                        <motion.div
                            key="button"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center justify-center"
                        >
                            <button
                                onClick={handleAiAnalysis}
                                disabled={aiLoading}
                                className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {aiLoading ? (
                                    <>
                                        <FaSpinner className="animate-spin" size={14} />
                                        Analizando patrones...
                                    </>
                                ) : (
                                    <>
                                        <FaMagic size={14} />
                                        Analizar patrones con IA
                                    </>
                                )}
                            </button>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="result"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <FaMagic size={12} className="text-purple-600" />
                                        <span className="text-sm font-semibold text-purple-700">
                                            Análisis de patrones
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setAiAnalysis(null)}
                                        className="text-xs text-purple-500 hover:text-purple-700 transition-colors"
                                    >
                                        Cerrar
                                    </button>
                                </div>
                                <div className="prose prose-sm max-w-none text-gray-700 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h1]:font-bold [&_h2]:font-semibold [&_h3]:font-semibold [&_p]:text-sm [&_li]:text-sm [&_strong]:text-gray-900">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiAnalysis}</ReactMarkdown>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default EventosDashboard;
